import React from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { Logo } from "./Logo";
import { Reveal } from "./primitives";
import { useLang } from "../i18n";
import { useConsent } from "../cookies/ConsentContext";
import { siteConfig, orComingSoon } from "../config/siteConfig";

export const Footer = () => {
  const { t } = useLang();
  const { openPreferences } = useConsent();
  const navLinks = [
    { to: "/", label: t.nav.home }, { to: "/for-brands", label: t.nav.brands },
    { to: "/for-publishers", label: t.nav.publishers }, { to: "/solutions", label: t.nav.solutions },
    { to: "/how-it-works", label: t.nav.how }, { to: "/about", label: t.nav.about },
    { to: "/insights", label: t.nav.insights }, { to: "/contact", label: t.nav.contact },
  ];
  const legal = [
    { to: "/privacy", label: t.footer.privacy },
    { to: "/terms", label: t.footer.terms },
    { to: "/cookie-policy", label: t.footer.cookie },
  ];

  return (
    <footer className="relative bg-wanm-dark text-white overflow-hidden" data-testid="footer">
      <div className="h-1.5 w-full wanm-gradient-bg" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 pt-20 pb-10">
        <div className="border-b border-white/10 pb-14">
          <h2 className="text-6xl md:text-8xl lg:text-[9rem] font-black tracking-tighter leading-[0.85]">
            <Reveal>{t.footer.big}</Reveal>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          <div className="col-span-2 md:col-span-1">
            <Logo variant="white" height={38} />
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-xs">{t.footer.desc}</p>
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-widest text-white/40 mb-5">{t.footer.nav}</h4>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/80 hover:text-wanm-orange transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-widest text-white/40 mb-5">{t.footer.legal}</h4>
            <ul className="space-y-3">
              {legal.map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/80 hover:text-wanm-orange transition-colors">{l.label}</Link></li>
              ))}
              <li>
                <button onClick={openPreferences} data-testid="footer-cookie-preferences"
                  className="text-white/80 hover:text-wanm-orange transition-colors text-left">
                  {t.footer.cookiePreferences}
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-widest text-white/40 mb-5">{t.footer.connect}</h4>
            <a href={siteConfig.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="WANM on LinkedIn"
              data-testid="social-linkedin"
              className="inline-flex items-center gap-3 text-white/80 hover:text-wanm-orange transition-colors group">
              <span className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center group-hover:wanm-gradient-bg group-hover:border-transparent transition-all">
                <Linkedin size={18} />
              </span>
              LinkedIn
            </a>
            <div className="mt-6 space-y-1 text-sm text-white/50">
              <p><span className="text-white/40">{t.footer.contact}: </span>{siteConfig.contact.email || t.footer.comingSoon}</p>
              <p><span className="text-white/40">Tel: </span>{siteConfig.contact.phone || t.footer.comingSoon}</p>
              <p><span className="text-white/40">Address: </span>{siteConfig.contact.address || t.footer.comingSoon}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/10 text-white/40 text-sm">
          <span>© {new Date().getFullYear()} WANM — We Are Nation Media. {t.footer.rights}</span>
          <span className="font-bold uppercase tracking-widest text-xs">{t.tagline}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
